/**
 * 新建收藏集表单组件
 */


import { zodResolver } from "@hookform/resolvers/zod";
import { FolderRoot, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useCreateFolderMutation } from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";

type CreateFolderResult = {
  action: "created";
  parentId: string | null;
};

interface CreateFolderProps extends DialogComponentProps<CreateFolderResult> {
  parentId?: string;
}

export function CreateFolder({ close, resolve, parentId }: CreateFolderProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(parentId || null);
  const createFolderMutation = useCreateFolderMutation();
  
  // 表单验证规则
  const formSchema = z.object({
    name: z.string()
      .min(1, { message: "文件夹名称不能为空" })
      .max(50, { message: "文件夹名称不能超过50个字符" }),
    description: z.string().max(200, { message: "描述不能超过200个字符" }).optional(),
    parentId: z.string().optional(), // 父文件夹ID（可选）
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      parentId: parentId || undefined,
    },
  });

  // 处理文件夹选择
  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolderId(folderId);
    form.setValue("parentId", folderId || undefined);
  };

  function onSubmit(values: z.infer<typeof formSchema>) {
    // 构建请求数据
    const data: { name: string; note?: string; parent_id?: string } = {
      name: values.name,
      note: values.description,
    };
    
    // 如果选择了父文件夹，添加 parent_id
    if (values.parentId) {
      data.parent_id = values.parentId;
    }

    createFolderMutation.mutate(data, {
      onSuccess: () => {
        form.reset();
        toast.success("文件夹创建成功");
        resolve({
          action: "created",
          parentId: selectedFolderId,
        });
      },
    });
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* 文件夹名称 */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">文件夹名称</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="请输入文件夹名称" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 文件夹描述（可选） */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">描述</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="请输入描述"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 父文件夹选择（可选） */}
          <FormItem>
            <Label className="text-xs text-muted-foreground">父文件夹</Label>
            <FolderSelector
              value={selectedFolderId}
              onChange={handleFolderSelect}
              placeholder="顶级目录"
              allowEmpty={true}
              emptyLabel="顶级目录"
              emptyIcon={FolderRoot}
            />
          </FormItem>
        </form>
      </Form>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={createFolderMutation.isPending}
        >
          取消
        </Button>
        <Button
          type="button"
          onClick={form.handleSubmit(onSubmit)}
          disabled={createFolderMutation.isPending}
        >
          确认
          {createFolderMutation.isPending && <Loader2 className="animate-spin" />}
        </Button>
      </DialogFooter>
    </>
  );
}
